"use client"

import React, { FC, useMemo, useState } from "react"
import { ControlledModal, Dialog, DialogContent, DialogFooter } from "@/components"
import { Button } from "@/elements"
import { DialogHeader } from "@/components/Dialog/DialogHeader"
import { FragmentType, graphql, useFragment } from "@/gql"
import { DropDownItem, TextInputWithDropdown } from "@/components/TextInputWithDropdown"
import { useLocationFilter } from "@/components/Filters/useLocationFilter"
import { useMutation } from "@apollo/client"
import { UpdateCreativeLocationDocument } from "@/gql/graphql"
import { stringify } from "wellknown"
import { captureException } from "@sentry/nextjs"

const EditLocationModalFragment = graphql(`
  fragment EditLocationModal on IndividualCreative {
    id
    __typename
    locationName
  }
`)

graphql(`
  mutation UpdateCreativeLocation($input: UpdateCreativeLocationInput!) {
    updateCreativeLocation(input: $input) {
      errors {
        field
        messages
      }
      creative {
        __typename
        id
        locationName
        location
      }
    }
  }
`)

interface Props {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  creative: FragmentType<typeof EditLocationModalFragment> | null | undefined
}

export const EditLocationModal: FC<Props> = ({ isOpen, onOpenChange, ...props }) => {
  const [update] = useMutation(UpdateCreativeLocationDocument)
  const [loading, setLoading] = useState(false)
  const creative = useFragment(EditLocationModalFragment, props.creative)
  const [location, setLocation] = useState({
    lat: "",
    lon: "",
    display: "",
  })
  const [textValue, setTextValue] = useState(creative?.locationName || "")
  const { locations } = useLocationFilter({ search: textValue, showRemote: false })

  const dropDownItems = useMemo(() => {
    return (
      locations?.map(loc => {
        return {
          display: loc.name!,
          lat: loc.lat!,
          lon: loc.lon!,
        }
      }) || []
    )
  }, [locations])

  const onClickDropDownItem = (item: DropDownItem) => {
    setLocation({
      lat: item.lat!,
      lon: item.lon!,
      display: item.display!,
    })
    setTextValue(item.display)
  }

  const onUpdate = async () => {
    if (location.lat && location.lon && !loading) {
      setLoading(true)
      const locationData = stringify({
        type: "Point",
        coordinates: [Number(location.lon), Number(location.lat)],
      })

      const response = await update({
        variables: {
          input: {
            id: creative?.id!,
            location: location.lon && location.lat ? locationData : null,
            locationName: location.display,
          },
        },
      })

      const errors = response.data?.updateCreativeLocation?.errors

      if (errors && errors.length > 0) {
        setLoading(false)
        console.log("errors", errors)
        captureException(errors)
        return
      } else {
        setLocation({
          lat: "",
          lon: "",
          display: "",
        })
        setTextValue("")
        setLoading(false)
        onOpenChange(false)
      }
    }
  }

  return (
    <ControlledModal
      open={isOpen}
      onOpenChange={onOpenChange}
      header={{ title: "Edit your location", variant: "default", fixed: true }}
      preventAutoFocus
      contentClassName="h-full"
      body={
        <div className="mb-12 flex h-[220px] flex-1 flex-col">
          <TextInputWithDropdown
            showMenuWithoutValue
            dropDownClassName="max-h-[220px] overflow-scroll"
            id="location-input"
            inputValue={textValue}
            dropdownItems={dropDownItems}
            inputProps={{
              placeholder: "Location",
              className: "text-primary-3 w-full",
              onChange: e => {
                setLocation({
                  lat: "",
                  lon: "",
                  display: "",
                })
                setTextValue(e.target.value)
              },
            }}
            onClickDropDownItem={onClickDropDownItem}
          />
        </div>
      }
      footer={{
        fixed: true,
        tertiaryButton: {
          children: "Cancel",
          onClick: () => onOpenChange(false),
        },
        primaryButton: {
          children: "Update",
          onClick: onUpdate,
          loading: loading,
        },
      }}
    />
  )
}
